import {
  CommentTextSchema,
  type ApprovalActionNoteOptions,
} from '@cims-online/shared'
import { Form } from 'antd'
import PayItemSelectProvider from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import CIMSTextArea from 'components/CIMSTextArea'
import { useEffect, useMemo, useState } from 'react'
import { useConfiguredFields } from '../../../../hooks/useConfiguredFields'
import ConfiguredFields from '../../../ConfiguredFields'
import type {
  ApprovalActionFormProps,
  ApprovalActionNoteValidation,
} from './types'

/** Optional whitespace becomes null; an unconfigured note is omitted from submission. */
function validateNote(
  options: ApprovalActionNoteOptions | null,
  value: unknown
): ApprovalActionNoteValidation {
  if (!options) return { data: undefined, issues: [], valid: true }
  if (typeof value !== 'string')
    return { data: undefined, issues: ['Note must be text'], valid: false }
  if (!options.required && value.trim() === '')
    return { data: null, issues: [], valid: true }

  const result = CommentTextSchema.safeParse(value)
  if (result.success) return { data: result.data, issues: [], valid: true }
  return {
    data: undefined,
    issues: result.error.issues.map(({ message }) => message),
    valid: false,
  }
}

/** Owns the decision note and submits it together with validated configured fields. */
function ApprovalActionFormContent({
  formId,
  onStateChange,
  onSubmit,
  pending,
  session,
}: ApprovalActionFormProps) {
  const action = session.target.action
  const {
    loading,
    onBlur,
    onChange,
    payItems,
    revealErrors,
    validation,
    values,
    visibleFieldIssues,
  } = useConfiguredFields(session.fieldSession)
  const [note, setNote] = useState(session.initialNote ?? '')
  const [noteTouched, setNoteTouched] = useState(false)
  const noteValidation = useMemo(
    () => validateNote(action.note, note),
    [action.note, note]
  )
  const valid = validation.valid && noteValidation.valid
  const noteError = noteTouched ? noteValidation.issues.join(' ') : undefined

  useEffect(() => {
    onStateChange({ loading, valid })
  }, [loading, onStateChange, valid])

  const handleSubmit = async () => {
    if (pending || loading) return
    if (!validation.valid || !noteValidation.valid) {
      revealErrors()
      setNoteTouched(true)
      return
    }

    await onSubmit({ ...validation.data, note: noteValidation.data })
  }

  return (
    <Form id={formId} layout="vertical" onFinish={handleSubmit}>
      <ConfiguredFields
        errors={visibleFieldIssues}
        fields={action.fields}
        formId={formId}
        onBlur={onBlur}
        onChange={onChange}
        payItems={payItems}
        pending={pending}
        values={values}
      />
      {action.note && (
        <Form.Item
          help={
            noteError ? (
              <span id={`${formId}-note-error`}>{noteError}</span>
            ) : undefined
          }
          htmlFor={`${formId}-note`}
          label="Note"
          required={action.note.required}
          validateStatus={noteError ? 'error' : undefined}
        >
          <CIMSTextArea
            id={`${formId}-note`}
            aria-describedby={noteError ? `${formId}-note-error` : undefined}
            aria-invalid={!!noteError}
            aria-required={action.note.required}
            disabled={pending}
            onBlur={() => setNoteTouched(true)}
            onChange={(event) => setNote(event.target.value)}
            value={note}
          />
        </Form.Item>
      )}
    </Form>
  )
}

/** Loads project pay items only when the approval's configured fields need them. */
function ApprovalActionForm(props: ApprovalActionFormProps) {
  return (
    <PayItemSelectProvider
      skip={
        !props.session.target.action.fields.some(
          ({ type }) => type === 'pay_items'
        )
      }
    >
      <ApprovalActionFormContent {...props} />
    </PayItemSelectProvider>
  )
}

export default ApprovalActionForm
